/**
 * Normalization layer: Euler V2 cluster → AaveV3-like response format.
 *
 * ## Conceptual mapping
 *
 * Euler V2 is pair-wise (vault A's LTV for collateral vault B), while
 * the target format has per-asset configs indexed by a key.
 *
 * The `config` map on each vault entry is keyed by **borrow vault address**
 * (lowercase). Each entry's LTV values describe this vault's collateral
 * standing when used with that borrow vault.
 *
 * This means:
 *   1. For vault V, `V.config[borrowVault]` = V's collateral LTV for that borrow vault
 *   2. All vaults with `config[B]` are eligible collateral for borrow vault B
 *
 * ### Example
 *
 * Euler cluster with 3 vaults:
 *   Vault W (WETH, 0xaaa...) - borrow vault, accepts [U, D] as collateral
 *   Vault U (USDC, 0xbbb...) - borrow vault, accepts [W] as collateral
 *   Vault D (DAI,  0xccc...) - collateral only
 *
 * Maps to:
 *   data["0xaaa..."]: {          ← WETH vault entry
 *     config: {
 *       "0xbbb...": { bcf: 0.82, cf: 0.87 }  ← WETH as collateral for USDC borrow vault
 *     }
 *   }
 *   data["0xbbb..."]: {          ← USDC vault entry
 *     config: {
 *       "0xaaa...": { bcf: 0.75, cf: 0.80 }  ← USDC as collateral for WETH borrow vault
 *     }
 *   }
 *   data["0xccc..."]: {          ← DAI vault entry
 *     config: {
 *       "0xaaa...": { bcf: 0.80, cf: 0.88 }  ← DAI as collateral for WETH borrow vault
 *     }
 *   }
 */

import { zeroAddress, type Address } from 'viem'
import { assetsPerShareAccumulator } from '../../accumulator'
import type { MarketAccumulator } from '../../../../types/apiReturnType'
import type { Cluster, VaultInfoFull, LTVInfo } from './types.js'

// ---- Target format types ----

export interface GenericCurrency {
  chainId: string
  address: string
  name?: string
  symbol?: string
  decimals: number
  [k: string]: any
}

export interface LenderConfigData {
  /** Lowercase borrow vault address that defines this config */
  category: string
  /** Counterparty vault name for display */
  label: string
  borrowCollateralFactor: number
  collateralFactor: number
  borrowFactor: number
  /**
   * Liquidation penalty as a fraction (e.g. 0.05 = 5%). For Euler this is
   * the seized collateral vault's `maxLiquidationDiscount`, which is the
   * maximum discount a liquidator can realise on that collateral.
   */
  liquidationPenalty: number
  closeFactor: number
  collateralDisabled?: boolean
  debtDisabled?: boolean
}

export interface EModeData {
  /** Lowercase borrow vault address */
  category: string
  label: string
  borrowCollateralFactor: number
  collateralFactor: number
  priceSource: string
}

export interface BaseYields {
  variableBorrowRate: number
  stableBorrowRate: number
  depositRate: number
}

/** Euler-specific metadata for a vault (analogous to Aave's aToken/vToken/sToken) */
export interface EulerVaultMetadata {
  /** The EVault contract address (ERC-4626 share token) */
  vault: string
  /** Debt token address (read-only ERC-20 representing borrows) */
  dToken: string
  /** Oracle used for pricing */
  oracle: string
  /** Interest rate model contract */
  interestRateModel: string
  /** Unit of account for this vault's oracle pricing */
  unitOfAccount: string
  /** EVC address this vault is registered with */
  evc: string
  /** Governor admin address */
  governorAdmin: string
  /**
   * Hook contract the governor installed, or undefined when none is set.
   *
   * A LIVE hook target does NOT disable the hooked operations — it routes them
   * through the hook, which may itself reject the caller (whitelist hooks are
   * the common shape). `maxDeposit` still reports capacity in that case, so the
   * market stays listed and this address is the handle an action layer needs to
   * pre-check. A ZERO target with the operation bit set is the disabled case,
   * and that is what `depositsEnabled` / `borrowingEnabled` below encode.
   */
  hookTarget?: string
  /** Raw `hookedOperations` bitmask — only present when non-zero. */
  hookedOperations?: number
}

export interface TokenEntry {
  /** Display name (e.g. "Euler V2 eWETH") */
  name: string
  asset: GenericCurrency
  // amounts
  totalDeposits: number
  totalDebtStable: number
  totalDebt: number
  totalDepositsUSD: number
  totalDebtStableUSD: number
  totalDebtUSD: number
  /** Borrow utilization (0..1) — `totalDebt / totalDeposits`. */
  utilization: number
  // rates
  depositRate: number
  variableBorrowRate: number
  stableBorrowRate: number
  averageStableBorrowRate: number
  // indices
  liquidityIndex: string
  variableBorrowIndex: string
  lastUpdateTimestamp: number
  /** `totalAssets / totalShares` from the lens — the vault's accumulator. */
  accumulator?: MarketAccumulator
  // Unified config keyed by counterparty vault address (lowercase).
  // LTV values always describe THIS vault's own collateral standing for the counterparty.
  // Entries come from both directions:
  //   - Reverse: borrow vaults that accept this vault as collateral (real LTV)
  //   - Forward: collateral vaults accepted by this borrow vault (LTV=0 if not mutual)
  config: { [counterpartyVaultAddress: string]: LenderConfigData }
  // flags
  collateralActive: boolean
  borrowingEnabled: boolean
  depositsEnabled: boolean
  hasStable: boolean
  isActive: boolean
  isFrozen: boolean
  // caps
  borrowCap: number
  supplyCap: number
  debtCeiling: number
  // eMode
  eMode: {
    label: string
    category: string
    borrowCollateralFactor: number
    collateralFactor: number
    priceSource: string
  }
  decimals: number
  // Euler-specific metadata
  params: { metadata: EulerVaultMetadata }
}

export interface NormalizedClusterResponse {
  data: { [vaultAddressLower: string]: TokenEntry }
  chainId: string
  /** Keyed by lowercase borrow vault address */
  eModes: { [borrowVaultAddress: string]: EModeData }
}

// ---- Scaling helpers ----

/** Euler LTV values are in basis points (10000 = 100%). Convert to 0..1 */
const LTV_SCALE = 10_000n

function ltvToNumber(ltv: bigint): number {
  return Number(ltv) / Number(LTV_SCALE)
}

/**
 * Euler APY values from irmInfo are in ray units (1e27 = 100%).
 * Convert to a simple decimal (0.05 = 5%).
 */
const RAY = 10n ** 27n

function rayToNumber(ray: bigint): number {
  return Number(ray) / Number(RAY)
}

function toTokenAmount(raw: bigint, decimals: bigint): number {
  return Number(raw) / 10 ** Number(decimals)
}

// ---- Token list type ----

export type GenericTokenList = { [addressLowerCase: string]: GenericCurrency }

// ---- Normalization options ----

export interface NormalizeOptions {
  chainId: string
  /**
   * Optional token list for looking up asset metadata.
   * If not provided, metadata comes from the vault's own fields.
   */
  tokenList?: GenericTokenList
}

// ---- Step 1: Identify borrow vaults ----

/**
 * A borrow vault is one that has an interest rate model set (non-zero)
 * and has collateral LTV entries with borrowLTV > 0.
 * Returns the set of lowercase borrow vault addresses.
 */
function identifyBorrowVaults(cluster: Cluster): Set<string> {
  const borrowVaults = new Set<string>()

  for (const [addr, info] of cluster.vaultData) {
    const hasIRM =
      info.interestRateModel &&
      info.interestRateModel.toLowerCase() !== zeroAddress
    const hasActiveCollateral = (info.collateralLTVInfo ?? []).some(
      (ltv) => ltv.borrowLTV > 0n,
    )

    if (hasIRM && hasActiveCollateral) {
      borrowVaults.add(addr.toLowerCase())
    }
  }

  return borrowVaults
}

// ---- Step 2: Build reverse LTV index ----

/**
 * For each vault, find all borrow vaults that accept it as collateral
 * and their LTV parameters.
 *
 * Returns: Map<collateralVault, Array<{ borrowVault, ltvInfo }>>
 */
function buildReverseLTVIndex(
  cluster: Cluster,
): Map<string, Array<{ borrowVault: string; ltv: LTVInfo }>> {
  const index = new Map<string, Array<{ borrowVault: string; ltv: LTVInfo }>>()

  for (const [addr, info] of cluster.vaultData) {
    const borrowVault = addr.toLowerCase()
    for (const ltv of info.collateralLTVInfo ?? []) {
      if (ltv.borrowLTV <= 0n) continue

      const collateral = ltv.collateral.toLowerCase()
      let entries = index.get(collateral)
      if (!entries) {
        entries = []
        index.set(collateral, entries)
      }
      entries.push({ borrowVault, ltv })
    }
  }

  return index
}

// ---- Step 3: Build config for each vault ----

/**
 * Builds the config for a vault using reverse LTV entries.
 * Keys are borrow vault addresses — for each, the LTV values describe
 * this vault's collateral standing when used with that borrow vault.
 */
function buildTokenConfig(
  vaultAddr: string,
  reverseLTV: Map<string, Array<{ borrowVault: string; ltv: LTVInfo }>>,
  borrowVaults: Set<string>,
  vaultData: Map<Address, VaultInfoFull>,
): { [counterpartyVaultAddress: string]: LenderConfigData } {
  const config: { [addr: string]: LenderConfigData } = {}

  // The liquidation penalty is a property of the collateral vault being
  // seized — i.e. THIS vault. `maxLiquidationDiscount` is 1e4-scaled (same
  // as LTVs), so reuse `ltvToNumber` to express it as a 0..1 fraction.
  const ownInfo = findInfo(vaultAddr, vaultData)
  const liquidationPenalty = ownInfo
    ? ltvToNumber(ownInfo.maxLiquidationDiscount)
    : 0

  // Pause state goes into the PER-CONFIG flags, not only the market-level
  // ones. Every consumer that decides whether a market can be a collateral or
  // a debt leg — the pair book, the config breakdown, exposure and risk
  // roll-ups — reads `collateralDisabled` / `debtDisabled` off the config
  // entry and lets it win over the market-level `depositsEnabled` /
  // `borrowingEnabled` (those are not comparable across lenders: Gearbox marks
  // every collateral-only token deposits-off). With only the market flags set,
  // a vault whose governor hooked deposits off kept ranking as collateral
  // everywhere, and one with borrow hooked off kept a borrowable self entry.
  // So: this vault as collateral is disabled when ITS deposits are off; its
  // self entry is disabled when ITS borrow is off. Everything off is
  // additionally `isFrozen` (market-level), already a hard gate downstream.
  const ownDepositsOff = ownInfo
    ? isOperationDisabled(ownInfo, OP_DEPOSIT)
    : false

  // Reverse: this vault as collateral for borrow vault [key]
  const reverseEntries = reverseLTV.get(vaultAddr) ?? []
  for (const { borrowVault, ltv } of reverseEntries) {
    if (!borrowVaults.has(borrowVault)) continue

    config[borrowVault] = {
      category: borrowVault,
      label: findInfo(borrowVault, vaultData)?.vaultName ?? borrowVault,
      borrowCollateralFactor: ltvToNumber(ltv.borrowLTV),
      collateralFactor: ltvToNumber(ltv.liquidationLTV),
      borrowFactor: 1.0,
      liquidationPenalty,
      closeFactor: 1,
      collateralDisabled: ownDepositsOff,
      debtDisabled: true,
    }
  }

  // Self-entry: if this vault is a borrow vault (controller), add itself
  // so it appears when searching for vaults with this controller as a mode
  if (borrowVaults.has(vaultAddr) && !config[vaultAddr]) {
    const selfInfo = findInfo(vaultAddr, vaultData)
    config[vaultAddr] = {
      category: vaultAddr,
      label: selfInfo?.vaultName ?? vaultAddr,
      borrowCollateralFactor: 0,
      collateralFactor: 0,
      borrowFactor: 1.0,
      liquidationPenalty,
      closeFactor: 1,
      // Borrow hooked off (zero hook target) ⇒ no new debt in this config.
      debtDisabled: selfInfo ? isOperationDisabled(selfInfo, OP_BORROW) : false,
      collateralDisabled: true,
    }
  }

  return config
}

// ---- Step 4: Build eModes ----

function buildEModes(
  borrowVaults: Set<string>,
  cluster: Cluster,
): { [borrowVaultAddress: string]: EModeData } {
  const eModes: { [borrowVaultAddress: string]: EModeData } = {}

  for (const addr of borrowVaults) {
    const info = findInfo(addr, cluster.vaultData)
    if (!info) continue

    const activeLTVs = info.collateralLTVInfo.filter((l) => l.borrowLTV > 0n)
    const avgBorrowLTV =
      activeLTVs.length > 0
        ? activeLTVs.reduce((sum, l) => sum + ltvToNumber(l.borrowLTV), 0) /
          activeLTVs.length
        : 0
    const avgLiqLTV =
      activeLTVs.length > 0
        ? activeLTVs.reduce(
            (sum, l) => sum + ltvToNumber(l.liquidationLTV),
            0,
          ) / activeLTVs.length
        : 0

    eModes[addr] = {
      category: addr,
      label: `${info.assetSymbol} Borrow (${info.vaultSymbol})`,
      borrowCollateralFactor: avgBorrowLTV,
      collateralFactor: avgLiqLTV,
      priceSource: info.oracle,
    }
  }

  return eModes
}

// ---- Step 5: Hooked operations ----

// EVK operation bits (evk `src/EVault/shared/Constants.sol`). Only the ones
// whose disablement changes a market flag are listed.
export const OP_DEPOSIT = 1 << 0
export const OP_MINT = 1 << 1
export const OP_BORROW = 1 << 6

/**
 * The EVK's own disabled-operation rule, verbatim (`Base.sol::isOperationDisabled`):
 * an operation is DISABLED only when its bit is hooked AND the hook target is
 * the zero address. A non-zero target means "gated", not "off" — see the
 * `hookTarget` note on {@link EulerVaultMetadata}.
 *
 * This is what `maxDeposit`/`maxMint`/`borrow` gate on, and it is INDEPENDENT of
 * the caps: a vault can be uncapped (`caps() = (0,0)` ⇒ supplyCap `uint256.max`)
 * and still refuse every deposit. Reconstructing depositability from the cap
 * headroom alone misses exactly that class — e.g. `ePT-STRCx-27AUG2026-1`
 * `0xb101113c…` on Ethereum, uncapped with `hookConfig() = (address(0), 32767)`
 * (every operation disabled) and `maxDeposit() == 0`.
 */
export function isOperationDisabled(
  info: Pick<VaultInfoFull, 'hookedOperations' | 'hookTarget'>,
  operation: number,
): boolean {
  const hookedOps = Number(info.hookedOperations ?? 0n)
  return (
    (hookedOps & operation) !== 0 &&
    info.hookTarget.toLowerCase() === zeroAddress
  )
}

// ---- Step 6: Build metadata ----

function buildMetadata(info: VaultInfoFull): EulerVaultMetadata {
  const hookedOperations = Number(info.hookedOperations ?? 0n)
  const hookTarget = info.hookTarget.toLowerCase()
  return {
    vault: info.vault.toLowerCase(),
    dToken: info.dToken.toLowerCase(),
    oracle: info.oracle.toLowerCase(),
    interestRateModel: info.interestRateModel.toLowerCase(),
    unitOfAccount: info.unitOfAccount.toLowerCase(),
    evc: info.evc.toLowerCase(),
    governorAdmin: info.governorAdmin.toLowerCase(),
    ...(hookedOperations !== 0 ? { hookedOperations } : {}),
    ...(hookTarget !== zeroAddress ? { hookTarget } : {}),
  }
}

// ---- Step 7: Assemble per-vault entry ----

function buildTokenEntry(
  info: VaultInfoFull,
  config: { [counterpartyVaultAddress: string]: LenderConfigData },
  collateralActive: boolean,
  borrowVaults: Set<string>,
  opts: NormalizeOptions,
): TokenEntry | undefined {
  if (!info.asset) return undefined
  const addr = info.asset.toLowerCase()
  const tokenMeta = opts.tokenList?.[addr]

  const asset: GenericCurrency = {
    chainId: opts.chainId,
    address: addr,
    name: tokenMeta?.name ?? info.assetName,
    symbol: tokenMeta?.symbol ?? info.assetSymbol,
    decimals: tokenMeta?.decimals ?? Number(info.assetDecimals),
  }

  // Extract current rates from irmInfo
  const irmData = info.irmInfo.interestRateInfo
  const currentRate = irmData.length > 0 ? irmData[irmData.length - 1] : null

  const depositRate = currentRate ? rayToNumber(currentRate.supplyAPY) : 0
  const variableBorrowRate = currentRate
    ? rayToNumber(currentRate.borrowAPY)
    : 0

  const totalDeposits = toTokenAmount(info.totalAssets, info.assetDecimals)
  const totalDebt = toTokenAmount(info.totalBorrowed, info.assetDecimals)

  // USD values: use liabilityPriceInfo if available
  const priceInfo = info.liabilityPriceInfo
  const hasPrice = !priceInfo.queryFailure && priceInfo.amountOutMid > 0n
  const pricePerUnit = hasPrice
    ? Number(priceInfo.amountOutMid) / Number(10n ** info.unitOfAccountDecimals)
    : 0

  // Flags
  const vaultAddrNorm = info.vault.toLowerCase()
  const isBorrowVault = borrowVaults.has(vaultAddrNorm)

  // Self eMode
  const selfEMode = isBorrowVault
    ? {
        label: `${info.assetSymbol} Borrow`,
        category: vaultAddrNorm,
        borrowCollateralFactor: 0,
        collateralFactor: 0,
        priceSource: info.oracle,
      }
    : {
        label: 'None',
        category: zeroAddress,
        borrowCollateralFactor: 0,
        collateralFactor: 0,
        priceSource: zeroAddress,
      }

  return {
    name: info.vaultName,
    asset,
    totalDeposits,
    totalDebtStable: 0,
    totalDebt,
    totalDepositsUSD: totalDeposits * pricePerUnit,
    totalDebtStableUSD: 0,
    totalDebtUSD: totalDebt * pricePerUnit,
    utilization: totalDeposits > 0 ? totalDebt / totalDeposits : 0,
    depositRate,
    variableBorrowRate,
    stableBorrowRate: 0,
    averageStableBorrowRate: 0,
    liquidityIndex: '0',
    variableBorrowIndex: '0',
    accumulator: assetsPerShareAccumulator(info.totalAssets, info.totalShares),
    lastUpdateTimestamp: Number(info.timestamp),
    config,
    collateralActive,
    // A governor can switch either side of a vault off through the hook system
    // without ever touching the caps, so these flags are read from
    // `hookedOperations`/`hookTarget` rather than assumed. A vault that takes no
    // deposits is not a collateral candidate and must not rank as one, however
    // attractive its rate — `maxDeposit`/`maxMint` return 0 for it on-chain.
    borrowingEnabled: isBorrowVault && !isOperationDisabled(info, OP_BORROW),
    // Gated on OP_DEPOSIT alone, not on `OP_DEPOSIT || OP_MINT`: every supply
    // route we encode calls `deposit`, so a vault that disabled only that leg is
    // unusable to us even while share-minting stays open.
    depositsEnabled: !isOperationDisabled(info, OP_DEPOSIT),
    hasStable: false,
    isActive: true,
    // Supply AND borrow both off is the closest EVK analogue of an Aave-style
    // freeze: the vault still prices and still lets existing positions unwind.
    isFrozen:
      isOperationDisabled(info, OP_DEPOSIT) &&
      isOperationDisabled(info, OP_MINT) &&
      isOperationDisabled(info, OP_BORROW),
    borrowCap: toTokenAmount(info.borrowCap, info.assetDecimals),
    // Supply switched off reports as ZERO CAPACITY, not merely as a false flag.
    // Consumers that rank markets (the pair book) can only compare capacity
    // across lenders — `depositsEnabled` means different things per provider
    // (Gearbox marks collateral-only tokens false because they have no lend
    // side, while they remain perfectly good collateral), so it cannot be a
    // cross-lender gate. Capacity can, and 0 is exactly what the vault's own
    // `maxDeposit`/`maxMint` return here. Note this is already the shape Euler
    // itself emits for a cap-disabled vault: `caps() = (1, …)` resolves to a
    // supply cap of 0.
    supplyCap: isOperationDisabled(info, OP_DEPOSIT)
      ? 0
      : toTokenAmount(info.supplyCap, info.assetDecimals),
    debtCeiling: 0,
    eMode: selfEMode,
    decimals: Number(info.assetDecimals),
    params: { metadata: buildMetadata(info) },
  }
}

/** Case-insensitive vault lookup */
function findInfo(
  addr: string,
  vaultData: Map<Address, VaultInfoFull>,
): VaultInfoFull | undefined {
  for (const [key, value] of vaultData) {
    if (key.toLowerCase() === addr) return value
  }
  return undefined
}

// ---- Main entry point ----

/**
 * Normalizes an Euler V2 cluster into the AaveV3-like response format.
 *
 * ## Config keyed by borrow vault address
 *
 * Each borrow vault in the cluster is a key in the config and eModes maps.
 * For each token/vault, `config[borrowVaultAddr]` contains its collateral
 * parameters when used as collateral for that borrow vault.
 *
 * This is the most native representation for Euler V2 where LTVs are
 * defined per collateral-vault pair.
 *
 * ## Data keyed by vault address
 *
 * The `data` map is keyed by **lowercase vault address** (not token symbol)
 * because multiple vaults in a cluster could share the same underlying
 * asset with different risk parameters.
 */
export function normalizeCluster(
  cluster: Cluster,
  opts: NormalizeOptions,
): NormalizedClusterResponse {
  // Step 1: Identify borrow vaults
  const borrowVaults = identifyBorrowVaults(cluster)

  // Step 2a: Build reverse LTV index (collateral → who accepts it)
  const reverseLTV = buildReverseLTVIndex(cluster)

  // Pre-compute which vaults are collateral-active (accepted by at least one borrow vault)
  const collateralActiveVaults = new Set<string>()
  for (const [collateral, entries] of reverseLTV) {
    if (entries.some((e) => borrowVaults.has(e.borrowVault))) {
      collateralActiveVaults.add(collateral)
    }
  }

  // Step 3+4: Build eModes
  const eModes = buildEModes(borrowVaults, cluster)

  // Step 5+6: Build per-vault entries
  const data: { [vaultAddressLower: string]: TokenEntry } = {}

  for (const [addr, info] of cluster.vaultData) {
    const norm = addr.toLowerCase()
    const config = buildTokenConfig(
      norm,
      reverseLTV,
      borrowVaults,
      cluster.vaultData,
    )
    const entry = buildTokenEntry(
      info,
      config,
      collateralActiveVaults.has(norm),
      borrowVaults,
      opts,
    )
    if (entry) data[norm] = entry
  }

  return {
    data,
    chainId: opts.chainId,
    eModes,
  }
}
